'use client';

import { useEffect, useRef, useState } from 'react';

import { getUserType, useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { getLogger } from '@ravago/shared/logging/client';
import { QuotationUtils, UserUtils } from '@ravago/shared/next-data-access/client';
import { SystemPageType } from '@ravago/shared/page-data/enums/system-page-type';
import { UserToggles } from '@ravago/shared/page-data/models/config';
import { NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import { PackagingGroup, ProductDetail } from '@ravago/shared/page-data/models/elements';
import { LeadTimeIndicator } from '@ravago/shared/page-data/models/elements/product-lead-time.model';
import { GuestRequestForQuotation, PreferredDeliveryTimeframe, VerifiedRequestForQuotation } from '@ravago/shared/page-data/models/features';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { Flyout, FlyoutActions, FlyoutContent, FlyoutHeader } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { DotsStepper } from '@ravago/shared/radiance/components/stepper/components/DotsStepper/DotsStepper';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { TranslateFn, useTranslations } from '@ravago/shared/translations/client';

import { useMutation } from '@tanstack/react-query';

import { ReadonlyURLSearchParams, usePathname, useSearchParams } from 'next/navigation';

import { usePathLanguageCode, usePathLocale } from '../../../../../../hooks/use-path-locale';
import { TrackingEvents, TrackingUtils } from '../../../../../../utils/tracking/tracking.utils';
import { SystemPageLink } from '../../../../system-page-link/system-page-link';
import {
   DEFAULT_PRODUCER_COLOR_CODE,
   OTHER_COLOR_ID,
   OTHER_PRODUCER_COLOR_CODE,
   UNKNOWN_COLOR_ID,
   UNKNOWN_PRODUCER_COLOR_CODE
} from '../../../constants';
import { useRequestForQuotationColor } from '../../../hooks/use-request-for-quotation-color';
import { useRequestForQuotationFlyoutState } from '../../../hooks/use-request-for-quotation-flyout-state';
import { useRequestForQuotationSteps } from '../../../hooks/use-request-for-quotation-steps';
import { useRequestForQuotationUom } from '../../../hooks/use-request-for-quotation-uom';
import { RequestForQuotationStep, RfqStep } from '../../../models/request-for-quotation-step';
import { ProductFlyoutColorDetails } from '../product-flyout-color-details';
import { ProductFlyoutProductTitle } from '../product-flyout-product-title';

import { FormValue as AddressDetailsFormValue, RfqFlyoutStepAddressDetails } from './steps/rfq-flyout-step-address-details';
import { FormValue as ContactDetailsFormValue, RfqFlyoutStepContactDetails } from './steps/rfq-flyout-step-contact-details';
import { FormValue as DeliveryAreaFormValue, RfqFlyoutStepDeliveryArea } from './steps/rfq-flyout-step-delivery-area';
import { RfqFlyoutStepFinalMessage } from './steps/rfq-flyout-step-final-message';
import {
   FormValue as QuotationDetailsFormValue,
   LocalFormValue as LocalQuotationDetailsFormValue,
   RfqFlyoutStepQuotationDetails
} from './steps/rfq-flyout-step-quotation-details';
import { RfqFlyoutStepRecognizedEmail } from './steps/rfq-flyout-step-recognized-email';
import { FormValue as SummaryFormValue, RfqFlyoutStepSummary } from './steps/rfq-flyout-step-summary';
import { FormValue as VerifyEmailFormValue, RfqFlyoutStepVerifyEmail } from './steps/rfq-flyout-step-verify-email';

interface RfqColor {
   colorId: number | undefined;
   colorName: string;
   producerColorCode: string | undefined;
   desiredColor: string | undefined;
   projectDescription: string | undefined;
}

interface Props {
   open: boolean;
   productId: string;
   productDescription: string;
   isCustomerProduct: boolean;
   defaultProduct?: ProductDetail;
   color: RfqColor | undefined;
   leadTimeIndicators?: LeadTimeIndicator[];
   leadTimeEnabled?: boolean;
   channelUOMs?: NormalizedUnitOfMeasure[];
   userToggles: UserToggles;
   from: 'PRODUCT_DETAILS' | 'MY_PRICES';
   type: 'QUOTATION' | 'QUICK_QUOTE';
   onClose: () => void;
   onComplete: () => void;
}

const getInitialQuotationDetailsFormValue = (
   searchParams: ReadonlyURLSearchParams,
   packagingGroups: PackagingGroup[]
): LocalQuotationDetailsFormValue | undefined => {
   const quantitiesFromSearchParams = searchParams.get('quantities');
   const packagingFromSearchParams = searchParams.get('packaging');
   const packagingDescriptionFromSearchParams = searchParams.get('packaging-description');

   if (!quantitiesFromSearchParams || !packagingFromSearchParams) return undefined;

   const defaultPackagingGroup = packagingGroups.find((packaging) => packaging.id.toString() === packagingFromSearchParams);

   const defaultQuantityBreaks = quantitiesFromSearchParams.split('-');
   const quantityBreakValues = defaultQuantityBreaks.map((quantity) => ({ value: quantity })).slice(0, 5); // Normally the length won't be more than 5, but just to be safe

   return {
      packaging: defaultPackagingGroup
         ? {
              id: defaultPackagingGroup.id.toString(),
              value: defaultPackagingGroup.description
           }
         : undefined,
      packagingDescription: packagingDescriptionFromSearchParams ?? '',
      quantityBreaks: quantityBreakValues
   };
};

export const getInitialStep = (fromLogin: boolean, userToggles: UserToggles) => {
   if (!fromLogin || userToggles.hasAnonymousToggle) return undefined;
   return userToggles.hasVerifiedToggle ? RfqStep.ADDRESS_DETAILS : RfqStep.CONTACT_DETAILS;
};

export const createQuotationLines = (
   quantityBreaks: number[],
   packagingId: number,
   packagingDescription: string | undefined,
   productId: string,
   uom: string,
   color: RfqColor,
   colorId: number,
   translate: TranslateFn
) =>
   quantityBreaks.map((qty) => ({
      productId: Number(productId),
      quantity: {
         amount: qty,
         unitOfMeasure: uom
      },
      preferredPackagingGroupId: packagingId,
      otherPackagingInfo: packagingDescription ?? '',
      colorId: color?.colorId === UNKNOWN_COLOR_ID ? OTHER_COLOR_ID : colorId,
      desiredColor:
         color?.colorId === UNKNOWN_COLOR_ID || color?.producerColorCode === UNKNOWN_PRODUCER_COLOR_CODE
            ? color?.projectDescription || translate('colors.unknown')
            : color?.desiredColor,
      producerColorCode: (() => {
         if (color?.producerColorCode === UNKNOWN_PRODUCER_COLOR_CODE || color?.producerColorCode === OTHER_PRODUCER_COLOR_CODE) {
            return 'Other';
         }
         if (color?.producerColorCode === DEFAULT_PRODUCER_COLOR_CODE) {
            return undefined;
         }
         return color?.producerColorCode;
      })()
   }));

export const RequestForQuotationFlyout = ({
   open,
   productId,
   productDescription,
   isCustomerProduct,
   defaultProduct,
   color,
   leadTimeIndicators = [],
   leadTimeEnabled = false,
   channelUOMs = [],
   userToggles,
   from,
   type,
   onClose,
   onComplete
}: Props) => {
   const locale = usePathLocale();
   const languageCode = usePathLanguageCode();
   const pathname = usePathname();
   const searchParams = useSearchParams();
   const translate = useTranslations();
   const { user, login } = useAuthentication();
   const { phone } = useBreakpoint();

   const { colors, packagingGroups, customer, countries, myProductsAndPricesRoute, loading } = useRequestForQuotationFlyoutState({
      productId,
      defaultProduct,
      isCustomerProduct
   });

   const {
      currentStep,
      setCurrentStep,
      currentStepIndex,
      nextStep,
      previousStep,
      reset: resetSteps,
      stepCount
   } = useRequestForQuotationSteps({
      userToggles,
      initialStep: getInitialStep(searchParams.get('open-rfq-after-login') === 'true', userToggles)
   });

   const requestQuotationMutation = useMutation({
      mutationFn: async (rfqData: VerifiedRequestForQuotation | GuestRequestForQuotation) =>
         'type' in rfqData ? QuotationUtils.requestQuotation(rfqData) : QuotationUtils.requestGuestQuotation(rfqData),
      onSuccess: () => {
         nextStep();
      }
   });

   const selectedColor = useRequestForQuotationColor({
      colors,
      colorId: color?.colorId,
      colorName: color?.colorName,
      producerColorCode: color?.producerColorCode
   });

   const [quotationDetailsFormValue, setQuotationDetailsFormValue] = useState<QuotationDetailsFormValue | undefined>();
   const [verifyEmailFormValue, setVerifyEmailFormValue] = useState<VerifyEmailFormValue | undefined>();
   const [addressDetailsFormValue, setAddressDetailsFormValue] = useState<AddressDetailsFormValue | undefined>();
   const [deliveryAreaFormValue, setDeliveryAreaFormValue] = useState<DeliveryAreaFormValue | undefined>();
   const [contactDetailsFormValue, setContactDetailsFormValue] = useState<ContactDetailsFormValue | undefined>();
   const [summaryFormValue, setSummaryFormValue] = useState<SummaryFormValue | undefined>();
   const [requestQuoteError, setRequestQuoteError] = useState(false);

   const stepRef = useRef<RequestForQuotationStep>(null);

   const { canChangeUom, effectiveChannelUOMs, selectedUomId, uom, onUomChange } = useRequestForQuotationUom({
      channelUOMs,
      userToggles,
      preferences: customer?.preferences
   });

   useEffect(() => {
      const isReturningFromLogin = searchParams.get('open-rfq-after-login') === 'true';

      if (isReturningFromLogin) {
         const cleanUrl = new URL(window.location.href);
         cleanUrl.searchParams.delete('open-rfq-after-login');
         window.history.replaceState({}, '', cleanUrl.toString());
      }
   }, [searchParams]);

   const resetValues = () => {
      setQuotationDetailsFormValue(undefined);
      setVerifyEmailFormValue(undefined);
      setAddressDetailsFormValue(undefined);
      setDeliveryAreaFormValue(undefined);
      setContactDetailsFormValue(undefined);
   };

   const trackEvent = (event: TrackingEvents) => {
      TrackingUtils.track(event, {
         productName: productDescription,
         productId,
         userLoggedIn: JSON.stringify(!!user),
         userType: getUserType(user)
      });
   };

   const handleCloseComplete = () => {
      resetValues();
      onComplete();
   };

   const handleCloseFlyout = () => {
      if (currentStep === RfqStep.FINAL_MESSAGE) {
         handleCloseComplete();
      }
      resetSteps();
      onClose();
   };

   const handleTryAgain = () => {
      requestQuotationMutation.reset();
      previousStep();
   };

   const createVerifiedRequest = (additionalInfo: string, colorId: number): VerifiedRequestForQuotation | undefined => {
      if (!color) {
         getLogger().warn("Can't request quote because color value is undefined");
         return undefined;
      }

      if (!quotationDetailsFormValue) {
         getLogger().warn("Can't request quote because form value is undefined");
         return undefined;
      }

      if (!addressDetailsFormValue) {
         getLogger().warn("Can't request quote because address form value is undefined");
         return undefined;
      }

      const quantityBreaks = quotationDetailsFormValue.quantityBreaks.map((qty) => Number(qty.value));
      const packagingId = Number(quotationDetailsFormValue.packaging.id);

      return {
         type,
         preferredDeliveryTimeframe: addressDetailsFormValue.timeframe.id as PreferredDeliveryTimeframe,
         additionalInfo,
         deliveryAddressId: addressDetailsFormValue.deliveryAddress.id,
         quotationRequestLines: createQuotationLines(
            quantityBreaks,
            packagingId,
            quotationDetailsFormValue.packagingDescription,
            productId,
            uom,
            color,
            colorId,
            translate
         )
      };
   };

   const createGuestRequest = (additionalInfo: string, colorId: number): GuestRequestForQuotation | undefined => {
      if (!color) {
         getLogger().warn("Can't request quote because color value is undefined");
         return undefined;
      }

      if (!quotationDetailsFormValue) {
         getLogger().warn("Can't request quote because form value is undefined");
         return undefined;
      }

      if (!deliveryAreaFormValue || !contactDetailsFormValue) {
         getLogger().warn("Can't request quote because form value is undefined");
         return undefined;
      }

      const quantityBreaks = quotationDetailsFormValue.quantityBreaks.map((qty) => Number(qty.value));
      const packagingId = Number(quotationDetailsFormValue.packaging.id);

      return {
         preferredDeliveryTimeframe: deliveryAreaFormValue.timeFrame.id as PreferredDeliveryTimeframe,
         additionalInfo,
         deliveryAddress: {
            country: deliveryAreaFormValue.country.value,
            city: deliveryAreaFormValue.city,
            postalCode: deliveryAreaFormValue.postalCode
         },
         contactInfo: {
            firstName: contactDetailsFormValue.firstName,
            lastName: contactDetailsFormValue.lastName,
            email: contactDetailsFormValue.email,
            language: languageCode,
            phoneNumber: `+${contactDetailsFormValue.phone.countryInfo?.prefix}${contactDetailsFormValue.phone.phone}`,
            company: {
               name: contactDetailsFormValue.companyName,
               countryIsoCode: contactDetailsFormValue.country.id
            }
         },
         quotationRequestLines: createQuotationLines(
            quantityBreaks,
            packagingId,
            quotationDetailsFormValue.packagingDescription,
            productId,
            uom,
            color,
            colorId,
            translate
         )
      };
   };

   const onQuotationDetailsSubmit = (value: QuotationDetailsFormValue) => {
      trackEvent('rfq_click_continue_flyout');
      setQuotationDetailsFormValue(value);
      nextStep();
   };

   const onAddressDetailsSubmit = (value: AddressDetailsFormValue) => {
      setAddressDetailsFormValue(value);
      nextStep();
   };

   const onVerifyEmailSubmit = async (value: VerifyEmailFormValue) => {
      setVerifyEmailFormValue(value);
      const { email } = value;
      const userExists = await UserUtils.checkIfUserExists(email);
      nextStep({ userExists });
   };

   const onContactDetailsSubmit = (value: ContactDetailsFormValue) => {
      trackEvent('rfq_click_delivery_flyout');
      setContactDetailsFormValue(value);
      nextStep();
   };

   const onDeliveryAreaSubmit = (value: DeliveryAreaFormValue) => {
      setDeliveryAreaFormValue(value);
      nextStep();
   };

   const onSummarySubmit = async (value: SummaryFormValue) => {
      trackEvent('rfq_click_request_quote_flyout');
      let colorId = selectedColor?.id;

      if (!colorId && type === 'QUICK_QUOTE') {
         colorId = -1; // fallback to "Other"
      }

      if (!colorId) {
         getLogger().warn("Can't create request because colorId is undefined");
         setRequestQuoteError(true);
         nextStep();
         return;
      }

      const request =
         !user || user.groups.includes(UserGroups.digitalPlatformGuest) || user.groups.includes(UserGroups.digitalPlatformPendingVerification)
            ? createGuestRequest(value.additionalInfo, colorId)
            : createVerifiedRequest(value.additionalInfo, colorId);

      if (!request) {
         getLogger().warn("Can't create request because required data is missing");
         setRequestQuoteError(true);
         nextStep();
         return;
      }

      requestQuotationMutation.mutate(request);
   };

   const onNextStep = async () => {
      if (currentStep === RfqStep.RECOGNIZED_EMAIL) {
         trackEvent('rfq_click_login_button_flyout');

         const params = new URLSearchParams(searchParams.toString());
         if (quotationDetailsFormValue) {
            params.set('flyout', 'open');
            params.set('open-rfq-after-login', 'true');
            params.set('packaging', quotationDetailsFormValue.packaging.id);
            if (quotationDetailsFormValue.packagingDescription) {
               params.set('packaging-description', quotationDetailsFormValue.packagingDescription);
            }
            params.set('quantities', quotationDetailsFormValue.quantityBreaks.map((v) => v.value).join('-'));
         }

         if (color) {
            params.set('color', color.colorName);
            if (color.producerColorCode) {
               params.set('producerColorCode', color.producerColorCode);
            }
         }

         login(`${pathname}?${params.toString()}`);
         return;
      }

      if (!stepRef.current) return;
      await stepRef.current.submit();
   };

   const onPreviousStep = () => {
      if (currentStep === RfqStep.RECOGNIZED_EMAIL) {
         trackEvent('rfq_click_back_button_flyout');
      }

      previousStep();
   };

   const handleCloseFinalStep = () => {
      resetValues();
      resetSteps();
      onComplete();
   };

   const getNextLabel = () => {
      switch (currentStep) {
         case RfqStep.QUOTATION_DETAILS:
            return translate('page-consumer.request-for-quotation.delivery-details');
         case RfqStep.VERIFY_EMAIL:
            return translate('page-consumer.request-for-quotation.continue');
         case RfqStep.RECOGNIZED_EMAIL:
            return translate('page-consumer.request-for-quotation.login');
         case RfqStep.CONTACT_DETAILS:
            return translate('page-consumer.request-for-quotation.delivery-details');
         case RfqStep.ADDRESS_DETAILS:
            return translate('page-consumer.request-for-quotation.quote-summary');
         case RfqStep.DELIVERY_AREA:
            return translate('page-consumer.request-for-quotation.quote-summary');
         case RfqStep.SUMMARY:
            return translate('page-consumer.request-for-quotation.send-quote-request');
         default:
            return undefined;
      }
   };

   const getPreviousLabel = () => {
      switch (currentStep) {
         case RfqStep.VERIFY_EMAIL:
            return translate('page-consumer.request-for-quotation.quote-details');
         case RfqStep.RECOGNIZED_EMAIL:
            return translate('page-consumer.request-for-quotation.back');
         case RfqStep.CONTACT_DETAILS:
            return translate('page-consumer.request-for-quotation.quote-details');
         case RfqStep.ADDRESS_DETAILS:
            return translate('page-consumer.request-for-quotation.quote-details');
         case RfqStep.DELIVERY_AREA:
            return translate('page-consumer.request-for-quotation.contact-details');
         case RfqStep.SUMMARY:
            return translate('page-consumer.request-for-quotation.delivery-details');
         default:
            return undefined;
      }
   };

   return (
      <Flyout open={open} closeOnOutsideClick onFlyoutClose={() => handleCloseFlyout()}>
         <FlyoutHeader>{translate('page-consumer.request-for-quotation.request-a-quote')}</FlyoutHeader>

         <FlyoutContent>
            {loading ? (
               <Box justify="center">
                  <Spinner />
               </Box>
            ) : (
               <Box direction="column" gap="md">
                  {[RfqStep.QUOTATION_DETAILS, RfqStep.ADDRESS_DETAILS, RfqStep.DELIVERY_AREA, RfqStep.SUMMARY].includes(currentStep) && (
                     <>
                        <ProductFlyoutProductTitle title={productDescription} />
                        {color && (
                           <ProductFlyoutColorDetails
                              colorId={color.colorId}
                              colorName={color.colorName}
                              producerColorCode={color.producerColorCode}
                              desiredColor={color?.desiredColor}
                           />
                        )}
                        {color?.projectDescription && (
                           <Box direction="column" gap="2xs">
                              <Typography component="span" variant="body-highlight">
                                 {translate('page-consumer.request-for-quotation.project-description')}
                              </Typography>
                              <Typography component="span" variant="body">
                                 {color?.projectDescription}
                              </Typography>
                           </Box>
                        )}
                     </>
                  )}
                  {currentStep === RfqStep.QUOTATION_DETAILS && (
                     <RfqFlyoutStepQuotationDetails
                        stepRef={stepRef}
                        uom={uom}
                        packagingGroups={packagingGroups ?? []}
                        leadTimeIndicators={leadTimeIndicators}
                        selectedColor={color}
                        isLeadTimeEnabled={leadTimeEnabled}
                        channelUOMs={effectiveChannelUOMs}
                        canChangeUom={canChangeUom}
                        selectedUomId={selectedUomId}
                        onUomChange={(newUomId: string) => {
                           onUomChange(newUomId);
                           setQuotationDetailsFormValue(undefined);
                        }}
                        initialFormValue={getInitialQuotationDetailsFormValue(searchParams, packagingGroups ?? [])}
                        defaultFormValue={quotationDetailsFormValue}
                        onSubmit={onQuotationDetailsSubmit}
                     />
                  )}
                  {currentStep === RfqStep.VERIFY_EMAIL && (
                     <RfqFlyoutStepVerifyEmail stepRef={stepRef} defaultFormValue={verifyEmailFormValue} onSubmit={onVerifyEmailSubmit} />
                  )}
                  {currentStep === RfqStep.RECOGNIZED_EMAIL && <RfqFlyoutStepRecognizedEmail />}
                  {currentStep === RfqStep.ADDRESS_DETAILS && (
                     <RfqFlyoutStepAddressDetails
                        stepRef={stepRef}
                        addresses={customer?.addresses ?? []}
                        defaultFormValue={addressDetailsFormValue}
                        onSubmit={onAddressDetailsSubmit}
                        leadTimeIndicators={leadTimeIndicators}
                        selectedColor={color}
                        packagingGroupId={quotationDetailsFormValue?.packaging?.id}
                        isLeadTimeEnabled={leadTimeEnabled}
                     />
                  )}
                  {currentStep === RfqStep.DELIVERY_AREA && (
                     <RfqFlyoutStepDeliveryArea
                        stepRef={stepRef}
                        countries={countries}
                        defaultFormValue={deliveryAreaFormValue}
                        leadTimeIndicators={leadTimeIndicators}
                        selectedColor={color}
                        packagingGroupId={quotationDetailsFormValue?.packaging?.id}
                        isLeadTimeEnabled={leadTimeEnabled && !userToggles.hasAnonymousToggle}
                        onSubmit={onDeliveryAreaSubmit}
                     />
                  )}
                  {currentStep === RfqStep.CONTACT_DETAILS && (
                     <RfqFlyoutStepContactDetails
                        stepRef={stepRef}
                        countries={countries}
                        email={verifyEmailFormValue?.email}
                        defaultFormValue={contactDetailsFormValue}
                        onSubmit={onContactDetailsSubmit}
                     />
                  )}
                  {currentStep === RfqStep.SUMMARY && (
                     <RfqFlyoutStepSummary
                        stepRef={stepRef}
                        setCurrentStep={setCurrentStep}
                        customer={customer}
                        userToggles={userToggles}
                        uom={uom}
                        quotationDetailsFormValue={quotationDetailsFormValue}
                        addressDetailsFormValue={addressDetailsFormValue}
                        deliveryAreaFormValue={deliveryAreaFormValue}
                        contactDetailsFormValue={contactDetailsFormValue}
                        summaryFormValue={summaryFormValue}
                        onSummaryChange={setSummaryFormValue}
                        onSubmit={onSummarySubmit}
                        leadTimeEnabled={leadTimeEnabled && !userToggles.hasAnonymousToggle}
                        leadTimeIndicators={leadTimeIndicators}
                        selectedColor={color}
                     />
                  )}
                  {currentStep === RfqStep.FINAL_MESSAGE && (
                     <RfqFlyoutStepFinalMessage requestQuoteError={requestQuotationMutation.data === false || requestQuoteError} />
                  )}
               </Box>
            )}
         </FlyoutContent>

         <FlyoutActions>
            {userToggles.hasVerifiedToggle && currentStep === RfqStep.FINAL_MESSAGE && from === 'PRODUCT_DETAILS' && (
               <SystemPageLink baseRoute={myProductsAndPricesRoute} type={SystemPageType.MY_PRODUCTS_AND_PRICES} locale={locale}>
                  <Button variant="primary" fullWidth onClick={() => trackEvent('rfq_click_my_quotes_flyout')}>
                     {translate('page-consumer.request-for-quotation.my-quotes')}
                  </Button>
               </SystemPageLink>
            )}
            {currentStep !== RfqStep.FINAL_MESSAGE && (
               <Button onClick={onNextStep} trailingIcon="arrow-right" disabled={requestQuotationMutation.isPending}>
                  {getNextLabel()}
               </Button>
            )}
            {currentStep === RfqStep.QUOTATION_DETAILS && from === 'PRODUCT_DETAILS' && (
               <Button variant="secondary" onClick={handleCloseFlyout} leadingIcon="arrow-left">
                  {translate('extended-color-picker.label')}
               </Button>
            )}
            {currentStep === RfqStep.QUOTATION_DETAILS && from !== 'PRODUCT_DETAILS' && (
               <Button variant="secondary" onClick={handleCloseComplete}>
                  {translate('request-for-quotation.close')}
               </Button>
            )}
            {currentStep === RfqStep.FINAL_MESSAGE && requestQuotationMutation.data === false && (
               <Button variant="primary" onClick={handleTryAgain} trailingIcon={!phone ? 'refresh' : ''}>
                  {translate('page-consumer.request-for-quotation.try-again')}
               </Button>
            )}
            {currentStep === RfqStep.FINAL_MESSAGE && (
               <Button variant="secondary" onClick={handleCloseFinalStep}>
                  {translate('request-for-quotation.close')}
               </Button>
            )}
            {currentStep !== RfqStep.QUOTATION_DETAILS && currentStep !== RfqStep.FINAL_MESSAGE && (
               <Button variant="secondary" onClick={onPreviousStep} leadingIcon="arrow-left">
                  {getPreviousLabel()}
               </Button>
            )}
            {currentStep !== RfqStep.FINAL_MESSAGE && <DotsStepper numberOfSteps={stepCount} currentStep={currentStepIndex} readOnly />}
         </FlyoutActions>
      </Flyout>
   );
};
